import {
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react'

export type LeftPanelMode = 'explorer' | 'search'

type LeftPanelModeContextValue = {
	mode: LeftPanelMode
	setMode: (mode: LeftPanelMode) => void
	openExplorer: () => void
	openSearch: () => void
}

const LeftPanelModeContext = createContext<LeftPanelModeContextValue | null>(
	null,
)

export const LeftPanelModeProvider = ({ children }: PropsWithChildren) => {
	const [mode, setMode] = useState<LeftPanelMode>('explorer')

	const openExplorer = useCallback(() => setMode('explorer'), [])
	const openSearch = useCallback(() => setMode('search'), [])

	const value = useMemo(
		() => ({
			mode,
			setMode,
			openExplorer,
			openSearch,
		}),
		[mode, openExplorer, openSearch],
	)

	return (
		<LeftPanelModeContext.Provider value={value}>
			{children}
		</LeftPanelModeContext.Provider>
	)
}

export const useLeftPanelMode = () => {
	const context = useContext(LeftPanelModeContext)
	if (!context) {
		return {
			mode: 'explorer' as LeftPanelMode,
			setMode: () => {},
			openExplorer: () => {},
			openSearch: () => {},
		}
	}
	return context
}
